import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, useQueryFetch } from 'sonata-react-components';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { deviceIdsImportFile } from '@/resources/audienceTargeting.resources';
import { DeviceIdsStyled } from './DeviceIds.styles';
import { ExampleIds } from './ExampleIds';

type Props = {
  onChange: (values: any) => void;
};

type State = {
  confirmedFile: string;
  file?: File;
};

function DeviceIds({ onChange }: Props) {
  const templateCsv = `${taptap.config.sonataStaticsUrlBase}/samples/deviceTargeting/exampleDeviceId.csv`;
  const { formatMessage } = useIntl();
  const fileInputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
  const token: Record<string, any> = {};
  const { user } = useTargetingContext();

  const [{ confirmedFile, file }, setState] = useState<State>({
    confirmedFile: '',
    file: undefined
  });

  const { isLoading } = useQueryFetch(
    ['deviceIdsImportFile', file],
    async () => {
      const res = await deviceIdsImportFile(token, user, file!);
      if (res && res.totalLines > 0) {
        onChange({
          s3Paths: [res.fileUrl],
          type: 'user-file'
        });
        setState(prevState => ({
          ...prevState,
          confirmedFile: res.filePath
        }));
      }
    },
    {
      enabled: !!file,
      staleTime: 0
    }
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = () => {
    const newFile = fileInputRef.current?.files?.[0];

    if (newFile) {
      setState(prev => ({
        ...prev,
        file: newFile
      }));
    }
  };

  const handleClickRemoveFile = () =>
    setState(prevState => ({
      ...prevState,
      confirmedFile: '',
      file: undefined
    }));

  return (
    <DeviceIdsStyled className="deviceIds">
      <div className="deviceIds__fileUpload">
        {isLoading || confirmedFile ? (
          <div className="deviceIds__fileUpload__confirmed">
            {isLoading ? (
              <i className="fa-solid fa-spinner fa-spin deviceIds__fileUpload__confirmedSpinner" />
            ) : (
              <i className="fa-regular fa-circle-check deviceIds__fileUpload__confirmedIcon" />
            )}
            <span className="deviceIds__fileUpload__confirmedName">{confirmedFile}</span>
            <ButtonUI
              className="deviceIds__fileUpload__confirmedDelete"
              icon="fa-regular fa-trash"
              id="deviceTargetingLoadingReset"
              onClick={() => handleClickRemoveFile()}
              shape="link"
              type="secondary"
            />
          </div>
        ) : (
          <>
            <div className="deviceIds__fileUpload__description">
              {formatMessage({
                defaultMessage: 'We support comma-separated CSV files with the following a',
                id: 'targeting.deviceIds.fileUpload.description'
              })}
              <span>
                {formatMessage({
                  defaultMessage: 'single column called Device id.',
                  id: 'targeting.deviceIds.fileUpload.descriptionBold'
                })}
              </span>
            </div>
            <ButtonUI
              className="deviceIds__fileUpload__templateButton"
              icon="fa-solid fa-arrow-down-to-line"
              id="deviceIdsImportDownload"
              onClick={() => window.open(templateCsv)}
              shape="link"
              text={formatMessage({
                defaultMessage: 'Download Template',
                id: 'targeting.deviceIds.fileUpload.download'
              })}
              type="secondary"
            />
            <ExampleIds />
            <ButtonUI
              className="deviceIds__fileUpload__button"
              icon="far fa-arrow-up-from-dotted-line"
              iconPosition="left"
              id="deviceIdsUploadConfirm"
              onClick={handleUploadClick}
              text={formatMessage({
                defaultMessage: 'Upload File',
                id: 'targeting.deviceIds.fileUpload.button'
              })}
              type="primary"
            />
          </>
        )}
      </div>
      <input accept=".csv" hidden onChange={handleFileInputChange} ref={fileInputRef} type="file" />
    </DeviceIdsStyled>
  );
}

export default DeviceIds;
